import { useEffect, useState, useMemo } from 'react';
import { Search, ChevronDown, ChevronUp, TrendingUp, TrendingDown } from 'lucide-react';
import { supabase } from '../services/supabase';
import { leerVarias, listaEnCastellano } from '../lib/leerVarias';
import { CATALOG, CATALOG_VERSION } from '../lib/evaluationCatalog';
import {
  tryBuildReportV2Model,
  scoreColor,
  type EvaluationReportV2,
} from '../lib/evaluationReportV2';
import EvaluationEvolutionBetween from '../components/EvaluationEvolutionBetween';
import { publishedForEvolution } from '../lib/evaluationSeries';
import EvaluationTrackingTable from '../components/EvaluationTrackingTable';

// PR-EV1/EV2 → n=8 élite (PR-N8-3b) — Vista admin de evaluaciones. Tres pestañas:
//  · Formales: histórico por alumno + evolución por pilar + detalle 3 niveles.
//  · Notas rápidas: listado global de quick_notes con filtros.
//  · Analítica: tops por pilar, proyección, distribución y regresiones.
// El catálogo v2 (4 pilares → 21 categorías → 60 subs) se resuelve por el
// `catalog_version` GUARDADO en cada evaluación (invariante N8-1); todos los
// números salen del modelo de vista v2 (evaluationReportV2), no del jsonb crudo.

interface EvalRow {
  id: string;
  student_id: string;
  student_name: string;
  monitor_name: string;
  period_start: string;
  period_end: string | null;
  report_number: string;
  // BUG 19: dirección ve los borradores en la tabla —son suyos, los crea al
  // asignar— pero no pueden ser un punto del gráfico: están todos a 0.
  status: string | null;
  detailed_raw: unknown; // jsonb crudo (para las series de evolución)
  catalog_version: number;
  // Modelo de vista resuelto por catalog_version; null = catálogo no soportado.
  model: EvaluationReportV2 | null;
  overall_comment: string | null;
}
interface QuickNoteRow {
  id: string;
  rating: number;
  note: string | null;
  created_at: string;
  session_id: string;
  session_date: string | null;
  student_name: string;
  trainer_name: string;
}
type Tab = 'formales' | 'seguimiento' | 'notas' | 'analitica';

function fmtDate(d: string | null) {
  if (!d) return '—';
  return new Date(`${d}T00:00:00`).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}
// Media de un pilar del modelo de vista v2 (number, nunca null en la práctica
// porque el modelo recomputa desde subs; 0 si faltara el bloque).
function pillarScoreOf(model: EvaluationReportV2 | null, pillarKey: string): number | null {
  return model?.pillars.find((p) => p.key === pillarKey)?.score ?? null;
}

// ============================================================
// LA PESTAÑA DE ANALÍTICA SACA CONCLUSIONES, Y UNA CONCLUSIÓN NO SE INVENTA
// ============================================================
// Esta página lee dos listas y lo que pinta con ellas no es solo una tabla:
//
//   «Sin alumnos con subida sostenida (mínimo 2 evaluaciones).»
//   «Sin caídas sostenidas (≥1 pt en las últimas 2).»
//
// La segunda es la que se mira para decidir con quién hay que sentarse. Si la
// lectura falla, `rows` se queda vacío y la analítica no dice «no he podido
// mirar»: dice que NO HAY NINGÚN ALUMNO CAYENDO. Y el bloque entero se esconde
// tras `rows.length === 0` con un «aún no hay evaluaciones para analizar», que
// en un club con dos temporadas de informes es falso de forma evidente para
// cualquiera menos para quien no sabe que hubo un error.
const QUE = {
  evaluaciones: 'las evaluaciones formales',
  notas: 'las notas rápidas',
} as const;

export default function EvaluacionesPage() {
  const [tab, setTab] = useState<Tab>('formales');
  const [rows, setRows] = useState<EvalRow[]>([]);
  const [notes, setNotes] = useState<QuickNoteRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fallidos, setFallidos] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [studentId, setStudentId] = useState<string>('');
  const [openId, setOpenId] = useState<string | null>(null);

  // Filtros de la pestaña Notas rápidas.
  const [nStudent, setNStudent] = useState('');
  const [nTrainer, setNTrainer] = useState('');
  const [nMin, setNMin] = useState(1);
  const [nMax, setNMax] = useState(10);
  const [nFrom, setNFrom] = useState('');
  const [nSession, setNSession] = useState('');

  useEffect(() => {
    (async () => {
      const { datos, fallidos: fallos } = await leerVarias([
        {
          que: QUE.evaluaciones,
          consulta: supabase
            .from('formal_evaluations')
            .select(
              'id, student_id, period_start, period_end, status, report_number, catalog_version, detailed_scores, overall_comment, student:profiles!student_id(full_name), monitor:profiles!monitor_id(full_name)'
            )
            .order('period_start', { ascending: false }),
        },
        {
          que: QUE.notas,
          consulta: supabase
            .from('quick_notes')
            .select(
              'id, rating, note, created_at, session_id, student:profiles!student_id(full_name), trainer:profiles!trainer_id(full_name), session:sessions!session_id(date)'
            )
            .order('created_at', { ascending: false }),
        },
      ]);
      const [evalsData, notesData] = datos;
      setFallidos(fallos);

      const parsed: EvalRow[] = (
        (evalsData ?? []) as unknown as {
          id: string;
          student_id: string;
          period_start: string;
          period_end: string | null;
          status: string | null;
          report_number: string;
          catalog_version: number | null;
          detailed_scores: unknown;
          overall_comment: string | null;
          student: { full_name: string } | null;
          monitor: { full_name: string } | null;
        }[]
      ).map((e) => ({
        id: e.id,
        student_id: e.student_id,
        student_name: e.student?.full_name ?? '',
        monitor_name: e.monitor?.full_name ?? '—',
        period_start: e.period_start,
        period_end: e.period_end,
        status: e.status,
        report_number: e.report_number,
        detailed_raw: e.detailed_scores,
        // Sin `catalog_version` la fila es VIEJA, no nueva: la columna existe
        // desde el v2, así que lo único que puede haber sin ella es un informe
        // de entonces. Caer al catálogo VIGENTE sería leer datos v2 con el
        // catálogo de hoy, que es justo la fabricación de notas que esta serie
        // cierra. El resto del código ya usaba `?? 2`; esto se había quedado
        // solo, y mientras el vigente era el v2 daba lo mismo.
        catalog_version: e.catalog_version ?? 2,
        model: tryBuildReportV2Model(e.detailed_scores, e.catalog_version ?? 2),
        overall_comment: e.overall_comment,
      }));
      setRows(parsed);

      const parsedNotes: QuickNoteRow[] = (
        (notesData ?? []) as unknown as {
          id: string;
          rating: number;
          note: string | null;
          created_at: string;
          session_id: string;
          student: { full_name: string } | null;
          trainer: { full_name: string } | null;
          session: { date: string } | null;
        }[]
      ).map((n) => ({
        id: n.id,
        rating: n.rating,
        note: n.note,
        created_at: n.created_at,
        session_id: n.session_id,
        session_date: n.session?.date ?? null,
        student_name: n.student?.full_name ?? '—',
        trainer_name: n.trainer?.full_name ?? '—',
      }));
      setNotes(parsedNotes);
      setLoading(false);
    })();
  }, []);

  const students = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((r) => m.set(r.student_id, r.student_name));
    return Array.from(m.entries()).sort((a, b) => a[1].localeCompare(b[1], 'es'));
  }, [rows]);

  const filtered = useMemo(
    () =>
      rows.filter(
        (r) =>
          (!studentId || r.student_id === studentId) &&
          r.student_name.toLowerCase().includes(search.toLowerCase())
      ),
    [rows, studentId, search]
  );

  // Evolución entre informes del alumno seleccionado. Reusa el componente único
  // EvaluationEvolutionBetween (N8-4); en el hub solo los pilares (showSubcharts
  // false) para no recargar el browse — los subgráficos viven en FichaAlumno.
  //
  // BUG 19: el filtro va AQUÍ y no en la lectura. Dirección tiene que seguir
  // viendo sus borradores en la tabla de abajo —los ha creado ella al asignar—
  // pero un borrador a 0 en el gráfico es un valle que no existió.
  const studentEvalRows = useMemo(() => {
    if (!studentId) return [];
    return publishedForEvolution(
      rows
        .filter((r) => r.student_id === studentId)
        .map((r) => ({
          period_start: r.period_start,
          report_number: r.report_number,
          detailed_scores: r.detailed_raw,
          status: r.status,
        }))
    );
  }, [rows, studentId]);

  // ── Notas rápidas: opciones de sesión + filtrado ──
  const noteSessionOptions = useMemo(() => {
    const map = new Map<string, string>();
    notes.forEach((n) => {
      if (!map.has(n.session_id)) {
        map.set(n.session_id, n.session_date ?? n.created_at.slice(0, 10));
      }
    });
    return Array.from(map.entries()).sort((a, b) => (a[1] < b[1] ? 1 : -1));
  }, [notes]);
  const noteStudents = useMemo(
    () => [...new Set(notes.map((n) => n.student_name))].sort((a, b) => a.localeCompare(b, 'es')),
    [notes]
  );
  const noteTrainers = useMemo(
    () => [...new Set(notes.map((n) => n.trainer_name))].sort((a, b) => a.localeCompare(b, 'es')),
    [notes]
  );
  const filteredNotes = useMemo(
    () =>
      notes.filter((n) => {
        if (nStudent && n.student_name !== nStudent) return false;
        if (nTrainer && n.trainer_name !== nTrainer) return false;
        if (n.rating < nMin || n.rating > nMax) return false;
        if (nFrom && n.created_at.slice(0, 10) < nFrom) return false;
        if (nSession && n.session_id !== nSession) return false;
        return true;
      }),
    [notes, nStudent, nTrainer, nMin, nMax, nFrom, nSession]
  );

  // ── Analítica (agregación a nivel de PILAR: 4 columnas, igual que la UI) ──
  // El detalle por categoría/sub vive en el visor por alumno (FichaAlumno); aquí
  // se agrega el club por pilar + overall, recalculado sobre el modelo v2 real.
  const analytics = useMemo(() => {
    // Un informe de catálogo no soportado no tiene notas legibles: queda FUERA
    // de las medias del club y del top-10. Contarlo como 0 (o como el 5.0 que
    // fabricaba el modelo viejo) contaminaría la analítica de todos.
    type ReadableRow = EvalRow & { model: EvaluationReportV2 };
    // LA ANALÍTICA NO MEZCLA CATÁLOGOS.
    //
    // Los tops, las medias del club y la distribución se calculan POR PILAR, y
    // recorriendo el catálogo vigente. Con dos versiones vivas, un informe de
    // otra versión no tiene esos pilares: entraba como 0 y hundía la media del
    // club y el top-10 con una nota que nadie puso. Y al revés, sus pilares
    // propios no se cuentan en ningún sitio.
    //
    // Se agregan solo los del catálogo vigente y se dice cuántos quedan fuera.
    // Es lo mismo que hacen las series: dos versiones no se comparan.
    const legibles = rows.filter((r): r is ReadableRow => r.model != null);
    const readable = legibles.filter((r) => r.model.catalogVersion === CATALOG_VERSION);
    const fueraPorVersion = legibles.length - readable.length;

    const byStudent = new Map<string, ReadableRow[]>();
    readable.forEach((r) => {
      const arr = byStudent.get(r.student_id) ?? [];
      arr.push(r);
      byStudent.set(r.student_id, arr);
    });

    const latest: { name: string; pillars: Record<string, number>; overall: number }[] = [];
    const series: { name: string; overalls: number[] }[] = [];
    byStudent.forEach((evs) => {
      const sorted = evs.slice().sort((a, b) => a.period_start.localeCompare(b.period_start));
      const last = sorted[sorted.length - 1];
      const pillars: Record<string, number> = {};
      CATALOG.forEach((p) => {
        pillars[p.key] = pillarScoreOf(last.model, p.key) ?? 0;
      });
      latest.push({ name: last.student_name, pillars, overall: last.model.overall });
      series.push({ name: last.student_name, overalls: sorted.map((s) => s.model.overall) });
    });

    // Top 10 por pilar.
    const topByPillar: Record<string, { name: string; score: number }[]> = {};
    CATALOG.forEach((p) => {
      topByPillar[p.key] = latest
        .map((l) => ({ name: l.name, score: l.pillars[p.key] }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);
    });

    // Proyección: subida sostenida del overall en las últimas 3 evaluaciones.
    // (sobre los informes del catálogo vigente, ver arriba)
    const projection = series
      .filter((s) => s.overalls.length >= 2)
      .map((s) => {
        const last3 = s.overalls.slice(-3);
        return { name: s.name, delta: last3[last3.length - 1] - last3[0] };
      })
      .filter((s) => s.delta > 0)
      .sort((a, b) => b.delta - a.delta)
      .slice(0, 10);

    // Regresión: caída ≥1pt en las últimas 2 evaluaciones.
    const regression = series
      .filter((s) => s.overalls.length >= 2)
      .map((s) => {
        const n = s.overalls.length;
        return { name: s.name, delta: s.overalls[n - 1] - s.overalls[n - 2] };
      })
      .filter((s) => s.delta <= -1)
      .sort((a, b) => a.delta - b.delta);

    // Distribución de scores por pilar (buckets sobre la última eval de cada alumno).
    const buckets = ['0-2', '2-4', '4-6', '6-8', '8-10'];
    const distribution = CATALOG.map((p) => {
      const counts = [0, 0, 0, 0, 0];
      latest.forEach((l) => {
        const sc = l.pillars[p.key];
        const idx = Math.min(4, Math.floor(sc / 2));
        counts[idx]++;
      });
      return { pillar: p.label, color: p.color, counts };
    });

    return { topByPillar, projection, regression, distribution, buckets, fueraPorVersion };
  }, [rows]);

  if (loading)
    return <p className="text-gold-primary font-oswald animate-pulse p-4">Cargando...</p>;

  const TABS: { key: Tab; label: string }[] = [
    { key: 'formales', label: 'Evaluaciones formales' },
    { key: 'seguimiento', label: 'Seguimiento' },
    { key: 'notas', label: 'Notas rápidas' },
    { key: 'analitica', label: 'Analítica' },
  ];

  return (
    <div className="space-y-6">
      <h2 className="font-oswald text-2xl text-white">Evaluaciones</h2>

      {/* Se avisa arriba, antes de las pestañas: el fallo afecta a las cuatro y
          quien entra a la analítica desde el menú no pasa por las otras. */}
      {fallidos.length > 0 && (
        <p className="text-error text-sm bg-error/10 rounded p-2" data-testid="eval-fallidos">
          No se han podido cargar {listaEnCastellano(fallidos)}. Lo que se ve en esta página está
          incompleto.
        </p>
      )}

      {/* ── Tabs ── */}
      <div className="flex gap-2 border-b border-bg-border" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            data-testid={`tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-oswald -mb-px border-b-2 ${
              tab === t.key
                ? 'border-gold-primary text-gold-primary'
                : 'border-transparent text-text-secondary'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ════════ TAB SEGUIMIENTO ════════ */}
      {/* Contador sin umbral para TODOS los alumnos (mig 194). Complementa —no
          sustituye— la cola del entrenador, que sigue filtrando por ≥8. */}
      {tab === 'seguimiento' && <EvaluationTrackingTable />}

      {/* ════════ TAB FORMALES ════════ */}
      {tab === 'formales' && (
        <div className="space-y-6" data-testid="panel-formales">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-2.5 text-text-secondary" size={18} />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar alumno..."
                className="w-full bg-bg-surface border border-bg-border rounded-lg pl-10 pr-3 py-2 text-white text-sm"
              />
            </div>
            <select
              value={studentId}
              onChange={(e) => setStudentId(e.target.value)}
              data-testid="student-select"
              className="bg-bg-surface border border-bg-border rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="">Todos los alumnos</option>
              {students.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {studentId && studentEvalRows.length > 0 && (
            <EvaluationEvolutionBetween evals={studentEvalRows} showSubcharts={false} />
          )}

          <div className="space-y-2" data-testid="eval-list">
            {filtered.length === 0 && (
              <p className="text-text-secondary font-inter text-sm" data-testid="eval-vacio">
                {fallidos.includes(QUE.evaluaciones)
                  ? 'No se han podido cargar las evaluaciones.'
                  : 'Sin evaluaciones.'}
              </p>
            )}
            {filtered.map((e) => {
              const isOpen = openId === e.id;
              return (
                <div
                  key={e.id}
                  className="bg-bg-surface border border-bg-border rounded-xl overflow-hidden"
                  data-testid={`eval-row-${e.id}`}
                >
                  <button
                    onClick={() => setOpenId(isOpen ? null : e.id)}
                    className="w-full flex items-center justify-between px-4 py-3"
                  >
                    <div className="text-left">
                      <p className="font-oswald text-white">{e.student_name}</p>
                      <p className="font-inter text-text-secondary text-xs">
                        {fmtDate(e.period_start)} → {fmtDate(e.period_end)} · {e.monitor_name}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* LOS PILARES DEL INFORME, NO LOS DEL CATÁLOGO VIGENTE.
                          Iteraba `CATALOG`, así que un informe de otra versión
                          pintaba un guion por cada pilar de hoy —«———»— como si
                          no tuviera notas, teniéndolas. */}
                      {(e.model?.pillars ?? []).map((p) => (
                        <span
                          key={p.key}
                          className="text-xs font-oswald w-7 text-center"
                          style={{ color: scoreColor(p.score) }}
                          title={p.label}
                        >
                          {p.score === null ? '—' : p.score.toFixed(1)}
                        </span>
                      ))}
                      {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-4 space-y-3" data-testid={`eval-detail-${e.id}`}>
                      {/* Catálogo no soportado: no hay notas legibles que desplegar. */}
                      {e.model == null && (
                        <p
                          className="font-inter text-warning text-sm"
                          data-testid={`eval-unsupported-${e.id}`}
                        >
                          Informe de un catálogo no soportado (v{e.catalog_version}; esta versión
                          lee v{CATALOG_VERSION}). No se muestran notas.
                        </p>
                      )}
                      {/* Detalle 3 niveles: pilar → categorías (media) → subs (nota). */}
                      {e.model?.pillars.map((p) => (
                        <div key={p.key} className="border-t border-bg-border pt-2">
                          <div className="flex items-center justify-between">
                            <p className="font-oswald text-sm" style={{ color: p.color }}>
                              {p.label}
                            </p>
                            <span
                              className="font-oswald text-sm"
                              style={{ color: scoreColor(p.score) }}
                            >
                              {p.score != null ? p.score.toFixed(1) : '—'}
                            </span>
                          </div>
                          <div className="mt-1 space-y-1">
                            {p.categories.map((c) => (
                              <div key={c.key}>
                                <div className="flex items-center justify-between">
                                  <span className="text-xs font-inter text-white">{c.label}</span>
                                  <span
                                    className="text-xs font-oswald"
                                    style={{ color: scoreColor(c.score) }}
                                  >
                                    {c.score != null ? c.score.toFixed(1) : '—'}
                                  </span>
                                </div>
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                                  {c.subs.map((s) => (
                                    <span key={s.key} className="text-[11px] text-text-secondary">
                                      {s.label}:{' '}
                                      <span style={{ color: scoreColor(s.score) }}>
                                        {s.score ?? '—'}
                                      </span>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                          {p.comment && (
                            <p className="font-inter text-xs text-text-secondary italic mt-1">
                              {p.comment}
                            </p>
                          )}
                        </div>
                      ))}
                      {e.overall_comment && (
                        <div className="border-t border-bg-border pt-2">
                          <p className="font-inter text-xs text-text-secondary">Comentario</p>
                          <p className="font-inter text-sm text-white">{e.overall_comment}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ════════ TAB NOTAS RÁPIDAS ════════ */}
      {tab === 'notas' && (
        <div className="space-y-4" data-testid="panel-notas">
          <div className="card flex flex-wrap gap-3 items-end">
            <div>
              <label className="label">Alumno</label>
              <select
                className="input h-9 text-sm w-44"
                value={nStudent}
                onChange={(e) => setNStudent(e.target.value)}
                data-testid="note-filter-student"
              >
                <option value="">Todos</option>
                {noteStudents.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Entrenador</label>
              <select
                className="input h-9 text-sm w-44"
                value={nTrainer}
                onChange={(e) => setNTrainer(e.target.value)}
                data-testid="note-filter-trainer"
              >
                <option value="">Todos</option>
                {noteTrainers.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Sesión</label>
              <select
                className="input h-9 text-sm w-44"
                value={nSession}
                onChange={(e) => setNSession(e.target.value)}
              >
                <option value="">Todas</option>
                {noteSessionOptions.map(([id, date]) => (
                  <option key={id} value={id}>
                    {date}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Desde</label>
              <input
                type="date"
                className="input h-9 text-sm"
                value={nFrom}
                onChange={(e) => setNFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="label">Nota mín.</label>
              <input
                type="number"
                min={1}
                max={10}
                className="input h-9 text-sm w-20"
                value={nMin}
                onChange={(e) => setNMin(Number(e.target.value))}
                data-testid="note-filter-min"
              />
            </div>
            <div>
              <label className="label">Nota máx.</label>
              <input
                type="number"
                min={1}
                max={10}
                className="input h-9 text-sm w-20"
                value={nMax}
                onChange={(e) => setNMax(Number(e.target.value))}
                data-testid="note-filter-max"
              />
            </div>
          </div>

          <div className="card p-0 overflow-hidden">
            <table className="w-full">
              <thead className="border-b border-bg-border bg-bg-elevated">
                <tr>
                  <th className="table-th">Alumno</th>
                  <th className="table-th">Entrenador</th>
                  <th className="table-th">Fecha</th>
                  <th className="table-th">Nota</th>
                  <th className="table-th">Comentario</th>
                </tr>
              </thead>
              <tbody data-testid="notes-list">
                {filteredNotes.length === 0 ? (
                  <tr>
                    <td
                      colSpan={5}
                      className="table-td text-center text-text-secondary py-10"
                      data-testid="notas-vacio"
                    >
                      {fallidos.includes(QUE.notas)
                        ? 'No se han podido cargar las notas rápidas.'
                        : 'Sin notas rápidas con esos filtros'}
                    </td>
                  </tr>
                ) : (
                  filteredNotes.map((n) => (
                    <tr key={n.id} className="table-row" data-testid={`note-row-${n.id}`}>
                      <td className="table-td font-medium">{n.student_name}</td>
                      <td className="table-td text-text-secondary text-sm">{n.trainer_name}</td>
                      <td className="table-td text-text-secondary text-xs">
                        {fmtDate(n.session_date ?? n.created_at.slice(0, 10))}
                      </td>
                      <td className="table-td">
                        <span className="font-oswald" style={{ color: scoreColor(n.rating) }}>
                          {n.rating}/10
                        </span>
                      </td>
                      <td className="table-td text-text-secondary text-sm max-w-xs truncate">
                        {n.note ?? '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ════════ TAB ANALÍTICA ════════ */}
      {tab === 'analitica' && (
        <div className="space-y-6" data-testid="panel-analitica">
          {fallidos.includes(QUE.evaluaciones) ? (
            /* Sin las evaluaciones no hay analítica que valga. Pintar «no hay
               nadie cayendo» con una lectura fallida es la conclusión contraria
               a la que hay que sacar, y esta pestaña existe para decidir con
               quién sentarse. */
            <p
              className="text-error text-sm bg-error/10 rounded p-2"
              data-testid="analitica-ilegible"
            >
              No se han podido cargar las evaluaciones formales, así que no hay analítica que
              enseñar. Lo que falta no es «ningún alumno en riesgo»: es la lectura.
            </p>
          ) : rows.length === 0 ? (
            <p className="text-text-secondary text-sm">
              Aún no hay evaluaciones formales para analizar.
            </p>
          ) : (
            <>
              {analytics.fueraPorVersion > 0 && (
                <p
                  className="text-text-secondary text-xs font-inter"
                  data-testid="analitica-fuera-por-version"
                >
                  {analytics.fueraPorVersion === 1
                    ? 'Hay 1 informe de un catálogo anterior que no entra en estos números: sus notas no son comparables con las de ahora.'
                    : `Hay ${analytics.fueraPorVersion} informes de catálogos anteriores que no entran en estos números: sus notas no son comparables con las de ahora.`}
                </p>
              )}

              {/* Top 10 por pilar */}
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                {CATALOG.map((p) => (
                  <div key={p.key} className="card" data-testid={`top-pillar-${p.key}`}>
                    <h3
                      className="font-oswald text-sm uppercase tracking-wider mb-3"
                      style={{ color: p.color }}
                    >
                      Top {p.label}
                    </h3>
                    {analytics.topByPillar[p.key].length === 0 ? (
                      <p className="text-text-secondary text-xs">Sin datos</p>
                    ) : (
                      <ol className="space-y-1">
                        {analytics.topByPillar[p.key].map((s, i) => (
                          <li key={i} className="flex justify-between text-sm">
                            <span className="text-white truncate">
                              {i + 1}. {s.name}
                            </span>
                            <span className="font-oswald" style={{ color: scoreColor(s.score) }}>
                              {s.score.toFixed(1)}
                            </span>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                ))}
              </div>

              {/* Proyección + Regresión */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="card" data-testid="projection-list">
                  <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-3 flex items-center gap-2">
                    <TrendingUp size={16} className="text-green-500" /> Mejor proyección
                  </h3>
                  {analytics.projection.length === 0 ? (
                    <p className="text-text-secondary text-xs">
                      Sin alumnos con subida sostenida (mínimo 2 evaluaciones).
                    </p>
                  ) : (
                    <ol className="space-y-1">
                      {analytics.projection.map((s, i) => (
                        <li key={i} className="flex justify-between text-sm">
                          <span className="text-white truncate">
                            {i + 1}. {s.name}
                          </span>
                          <span className="font-oswald text-green-500">+{s.delta.toFixed(1)}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>

                <div className="card" data-testid="regression-list">
                  <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-3 flex items-center gap-2">
                    <TrendingDown size={16} className="text-red-500" /> Alumnos con regresión
                  </h3>
                  {analytics.regression.length === 0 ? (
                    <p className="text-text-secondary text-xs">
                      Sin caídas sostenidas (≥1 pt en las últimas 2).
                    </p>
                  ) : (
                    <ol className="space-y-1">
                      {analytics.regression.map((s, i) => (
                        <li key={i} className="flex justify-between text-sm">
                          <span className="text-white truncate">
                            {i + 1}. {s.name}
                          </span>
                          <span className="font-oswald text-red-500">{s.delta.toFixed(1)}</span>
                        </li>
                      ))}
                    </ol>
                  )}
                </div>
              </div>

              {/* Distribución de scores por pilar */}
              <div className="card" data-testid="distribution">
                <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
                  Distribución de scores por pilar
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                  {analytics.distribution.map((d) => {
                    const maxCount = Math.max(1, ...d.counts);
                    return (
                      <div key={d.pillar}>
                        <p className="text-sm font-oswald mb-2" style={{ color: d.color }}>
                          {d.pillar}
                        </p>
                        <div className="space-y-1">
                          {d.counts.map((c, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <span className="text-xs text-text-secondary w-10">
                                {analytics.buckets[i]}
                              </span>
                              <div className="flex-1 bg-bg-elevated rounded h-3 overflow-hidden">
                                <div
                                  className="h-3 rounded"
                                  style={{
                                    width: `${(c / maxCount) * 100}%`,
                                    background: d.color,
                                  }}
                                />
                              </div>
                              <span className="text-xs text-text-secondary w-5 text-right">
                                {c}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
